import React, {useEffect, useRef} from 'react';
import {useVirtualizer} from '@tanstack/react-virtual';
import {faUserShield as PermissionsIcon} from '@fortawesome/pro-solid-svg-icons';
import {List} from '@mui/material';
import {
	ScrollBorder,
	scrollBorderClasses,
} from '@netx/core-components/ScrollBorder';
import {EmptyStateFallbackMessage} from '@netx/core-components/EmptyStateFallbackMessage';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import type {IUserGroupAutoListItem} from '@netx/core-user-next/schema';
import {USER_SELECTOR_LABEL} from '../../locales';
import {UserSelectorListItem} from './UserSelectorListItem';

const UserSelectorListItemsRoot = styled(ScrollBorder)(({theme}) => ({}));

const UserSelectorListFallbackMessage = styled(EmptyStateFallbackMessage)(
	({theme}) => ({
		background: 'transparent',
	}),
);

const UserSelectorListItemsWrapper = styled(List)(({theme}) => ({
	width: '100%',
}));

const UserSelectorListItemsItemWrapper = styled(
	'div',
	{},
)(({theme}) => ({
	position: 'absolute',
	top: 0,
	left: 0,
	display: 'flex',
	width: '100%',
}));

export type UserSelectorListItemsProps = {
	disableNone?: boolean;
	items: IUserGroupAutoListItem[];
	hideSubtitle?: boolean;
	onRemove?: (item: IUserGroupAutoListItem) => void;
};

const UserSelectorListItemsPrimitive = ({
	disableNone,
	hideSubtitle,
	items,
	onRemove,
	...props
}: UserSelectorListItemsProps) => {
	const ref = useRef<HTMLDivElement>(null);
	const scrollRef = useRef<HTMLDivElement>(null);

	const rowVirtualizer = useVirtualizer({
		count: items.length,
		getScrollElement: () => scrollRef.current,
		estimateSize: () => (!hideSubtitle ? 60 : 56),
		overscan: 2,
	});

	useEffect(() => {
		rowVirtualizer.measure();
	}, [hideSubtitle]);

	const totalSize = rowVirtualizer.getTotalSize() + 8 * 2;

	return (
		<UserSelectorListItemsRoot
			// className={clsx(classes.root, className)}
			ref={ref}
			innerRef={scrollRef}
		>
			{items.length > 0 ? (
				<UserSelectorListItemsWrapper
					style={{
						minHeight: totalSize,
						height: totalSize,
					}}
				>
					{rowVirtualizer.getVirtualItems().map((virtualItem, index) => {
						const item = items[virtualItem.index];
						return (
							<UserSelectorListItemsItemWrapper
								key={`${item.type}-${item.name}`}
								data-index={virtualItem.index}
								style={{
									transform: `translatey(${virtualItem.start}px)`,
								}}
							>
								<UserSelectorListItem
									dense={!hideSubtitle}
									data-index={virtualItem.index}
									hideSubtitle={hideSubtitle}
									{...item}
									onRemove={
										(!disableNone || item.id !== -1) && onRemove
											? () => onRemove(item)
											: undefined
									}
								/>
							</UserSelectorListItemsItemWrapper>
						);
					})}
				</UserSelectorListItemsWrapper>
			) : (
				<UserSelectorListFallbackMessage
					icon={PermissionsIcon}
					title={
						disableNone
							? USER_SELECTOR_LABEL.ALL_USERS
							: USER_SELECTOR_LABEL.NO_USERS_SELECTED
					}
				/>
			)}
		</UserSelectorListItemsRoot>
	);
};

export const UserSelectorListItems = UserSelectorListItemsPrimitive;

export default UserSelectorListItems;
